import { Controller } from "@hotwired/stimulus";

// Donor checkout goal-boost module (DO-REQ-001..005, Carter MVT redesign
// 2026-07-10). Rendered on every donation checkout when the in-house MVT is on
// and the student still has room to their goal. It frames the
// ask around the student's remaining amount, offers fixed preset boosts
// (+$10/+$25/+$50) capped to what is left, and a dominant primary CTA that adds
// the full remaining amount so the student reaches their goal.
//
// "remaining" (goal - raised) comes from the server (Donations::StudentGoalBoost)
// and is fixed for the page. The donor's own chosen donation is the "base"; the
// module applies the selected delta on top of the base by writing the new total
// into the existing amount field, so the form's own summary/total recompute with
// no payment code touched. "Only $X to go" and the CTA update as the donor
// selects, giving immediate, tangible feedback.
export default class extends Controller {
  static targets = ["progress", "presets", "preset", "reach"];
  static values = {
    remaining: Number,
    amountSelector: { type: String, default: "#other-amount" },
    campaignId: Number,
  };

  connect() {
    this.selectedDelta = 0;
    this.selectedOption = null;
    this.shownTracked = false;
    this.suppressObserver = false;
    this.suppressTimer = null;

    this.donationAmountNode = document.querySelector(".step-summary__donation .amount");
    if (!this.donationAmountNode) return;

    this.baseAmount = this.readDonationAmount();

    this.observer = new MutationObserver(() => this.onDonorAmountChange());
    this.observer.observe(this.donationAmountNode, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    this.evaluate();
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    if (this.suppressTimer) clearTimeout(this.suppressTimer);
  }

  // The donor changed their base amount on the form (not one of our own writes):
  // re-anchor the base, drop any active selection, and re-evaluate.
  onDonorAmountChange() {
    if (this.suppressObserver) return;

    this.baseAmount = this.readDonationAmount();
    this.selectedDelta = 0;
    this.selectedOption = null;
    this.evaluate();
  }

  // Decides whether the module is shown at all, then renders the copy/controls.
  // Hidden until the donor has entered an amount, and when that base already
  // meets or passes the goal.
  evaluate() {
    if (this.baseAmount <= 0 || this.baseToGo() <= 0) {
      this.hide();
      return;
    }

    this.show();
    this.render();
    this.trackShownOnce();
  }

  render() {
    const toGoNow = this.remainingValue - this.currentTotal();
    if (toGoNow > 0) {
      this.progressTarget.innerHTML = `Only <strong>$${this.money(toGoNow)}</strong> to go.`;
    } else {
      this.progressTarget.innerHTML = `You'll help them <strong>reach their goal!</strong>`;
    }

    this.renderPresets();
    this.renderReach();
  }

  // Cap rule: hide any preset that meets or exceeds what is left from the base
  // (that amount is already covered by the primary "Reach Their Goal" CTA).
  renderPresets() {
    const room = this.baseToGo();
    this.presetTargets.forEach((button) => {
      const amount = Number(button.dataset.donorNudgeAmountParam);
      button.hidden = amount >= room;
      button.classList.toggle("active", this.selectedOption === this.presetKey(amount));
    });
  }

  renderReach() {
    const delta = this.baseToGo();
    this.reachTarget.textContent = `Reach Their Goal (+$${this.money(delta)})`;
    this.reachTarget.classList.toggle("active", this.selectedOption === "reach_goal");
  }

  selectPreset(event) {
    const amount = Number(event.params.amount);
    this.selectOption(this.presetKey(amount), amount);
  }

  reachGoal() {
    this.selectOption("reach_goal", this.baseToGo());
  }

  // Options are mutually exclusive. Selecting one is a positive action; tapping
  // the already-active option is a no-op so an accidental second tap (common on
  // mobile) never silently clears the donor's choice. Only "No Thanks" clears.
  selectOption(option, delta) {
    if (this.selectedOption === option) return;

    this.selectedOption = option;
    this.selectedDelta = delta;
    this.writeAmount(this.baseAmount + delta);
    this.track("donor_nudge_accepted");
    this.render();
  }

  decline() {
    this.selectedOption = null;
    this.selectedDelta = 0;
    this.writeAmount(this.baseAmount);
    this.track("donor_nudge_declined");
    this.hide();
  }

  // Routes the new total through the existing custom-amount field so the form's
  // own summary recomputation runs. Never touches payment state. The observer is
  // suppressed briefly so our own write is not mistaken for a donor edit.
  writeAmount(value) {
    this.suppressObserver = true;
    if (this.suppressTimer) clearTimeout(this.suppressTimer);
    this.suppressTimer = setTimeout(() => {
      this.suppressObserver = false;
    }, 120);

    const labels = Array.from(document.querySelectorAll(".donate-form__radio-button"));
    const otherLabel = labels.find((label) => {
      const input = label.querySelector("input");
      return input && input.value === "Other";
    });

    if (otherLabel) {
      labels.forEach((label) => label.classList.remove("active"));
      otherLabel.classList.add("active");
      const otherInput = otherLabel.querySelector("input");
      if (otherInput) otherInput.checked = true;
    }

    const customWrap = document.querySelector(".donate-form__amount--custom");
    if (customWrap) customWrap.classList.add("active");

    const amountInput = document.querySelector(this.amountSelectorValue);
    if (amountInput) {
      amountInput.disabled = false;
      amountInput.value = Number(value).toFixed(2);
      amountInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  currentTotal() {
    return this.baseAmount + this.selectedDelta;
  }

  // Amount still needed to reach the goal from the donor's base contribution.
  baseToGo() {
    return this.remainingValue - this.baseAmount;
  }

  readDonationAmount() {
    if (!this.donationAmountNode) return 0;
    return parseFloat(this.donationAmountNode.textContent.replace(/[^0-9.]/g, "")) || 0;
  }

  presetKey(amount) {
    return `preset_${amount}`;
  }

  money(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  trackShownOnce() {
    if (this.shownTracked) return;
    this.shownTracked = true;
    this.track("donor_nudge_shown");
  }

  // Reuses the app's existing GTM dataLayer pipeline (DO-REQ-012). No donor PII
  // is sent: only the campaign, selected option, and the amounts. No-op when
  // dataLayer is absent (e.g. consent not granted).
  track(eventName) {
    if (typeof window === "undefined" || !Array.isArray(window.dataLayer)) return;

    window.dataLayer.push({
      event: eventName,
      donor_nudge_campaign_id: this.hasCampaignIdValue ? this.campaignIdValue : null,
      donor_nudge_option: this.selectedOption,
      donor_nudge_original_amount: this.baseAmount,
      donor_nudge_total_amount: this.currentTotal(),
      donor_nudge_remaining_to_goal: this.remainingValue,
    });
  }

  show() {
    this.element.hidden = false;
  }

  hide() {
    this.element.hidden = true;
  }
}
